/**
 * Gráficos em SVG puro.
 *
 * Sem biblioteca de terceiros: são três formas (área, barras e arco de
 * progresso) e uma dependência de gráficos custaria mais bytes do que o resto
 * do painel inteiro. SVG também herda as cores do tema automaticamente.
 *
 * Convenções: a série principal usa a cor da marca, a secundária um tom neutro,
 * e vermelho aparece só quando algo passa de um limite. Cor por si só nunca
 * carrega o significado; sempre há rótulo junto.
 */

import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/cn'

export interface SeriesPoint {
  label: string
  value: number
  /** Segunda série opcional, desenhada empilhada abaixo da primeira. */
  secondary?: number
}

const PADDING = { top: 12, right: 8, bottom: 22, left: 44 }

/** Escala de eixo com valores redondos, para o rótulo não sair como "8347". */
function niceScale(max: number, ticks = 4): { max: number; step: number } {
  if (max <= 0) return { max: 1, step: 0.25 }

  const rawStep = max / ticks
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalized = rawStep / magnitude

  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude
  return { max: step * ticks, step }
}

export function AreaChart({
  data,
  height = 180,
  formatValue = (v) => String(Math.round(v)),
  color = 'var(--color-brand)',
  secondaryColor = 'var(--color-content-muted)',
  primaryLabel,
  secondaryLabel,
}: {
  data: SeriesPoint[]
  height?: number
  formatValue?: (value: number) => string
  color?: string
  secondaryColor?: string
  primaryLabel?: string
  secondaryLabel?: string
}) {
  const gradientId = useId()
  const [hover, setHover] = useState<number | null>(null)

  /*
    A largura vem do container, não é um número fixo.

    Antes o desenho tinha 640 unidades de largura e o SVG esticava mantendo a
    proporção, então num card mais largo que isso a área útil parava no meio e
    sobravam duas faixas vazias nas laterais. Medir o espaço disponível resolve
    e ainda faz uma unidade do desenho valer exatamente um pixel, que é o que
    permite pôr a caixa de valores no lugar certo.
  */
  const areaRef = useRef<HTMLDivElement>(null)
  const [largura, setLargura] = useState(640)

  useLayoutEffect(() => {
    const elemento = areaRef.current
    if (!elemento) return

    const medir = () => setLargura(Math.max(320, Math.round(elemento.clientWidth)))
    medir()

    const observador = new ResizeObserver(medir)
    observador.observe(elemento)
    return () => observador.disconnect()
  }, [])

  const width = largura
  const innerWidth = width - PADDING.left - PADDING.right
  const innerHeight = height - PADDING.top - PADDING.bottom

  const scale = useMemo(() => {
    const max = Math.max(
      ...data.map((d) => Math.max(d.value, d.secondary ?? 0)),
      0,
    )
    return niceScale(max)
  }, [data])

  const xAt = (index: number) =>
    PADDING.left + (data.length === 1 ? innerWidth / 2 : (index / (data.length - 1)) * innerWidth)
  const yAt = (value: number) => PADDING.top + innerHeight - (value / scale.max) * innerHeight

  const buildPath = (key: 'value' | 'secondary') => {
    const points = data.map((point, index) => {
      const value = key === 'value' ? point.value : (point.secondary ?? 0)
      return `${index === 0 ? 'M' : 'L'} ${xAt(index)} ${yAt(value)}`
    })
    return points.join(' ')
  }

  const areaPath = `${buildPath('value')} L ${xAt(data.length - 1)} ${PADDING.top + innerHeight} L ${xAt(0)} ${PADDING.top + innerHeight} Z`

  const hasSecondary = data.some((d) => d.secondary !== undefined)
  const hovered = hover !== null ? data[hover] : null

  /*
    O caso sem dados fica dentro do mesmo container de propósito.

    Saindo por cima, como era antes, o elemento medido não existia no primeiro
    render de um gráfico que ainda espera os dados. A medida ficava presa no
    valor de partida e o desenho nascia com a largura errada quando os números
    chegavam.
  */
  if (data.length === 0) {
    return (
      <div ref={areaRef} className="relative">
        <div
          className="flex items-center justify-center rounded-lg bg-surface-base text-sm text-content-muted"
          style={{ height }}
        >
          Sem dados no período
        </div>
      </div>
    )
  }

  return (
    <div ref={areaRef} className="relative">
      {primaryLabel || secondaryLabel ? (
        <div className="mb-2 flex items-center gap-4 text-[11px]">
          {primaryLabel ? (
            <span className="flex items-center gap-1.5 text-content-secondary">
              <span className="size-2 rounded-full" style={{ backgroundColor: color }} />
              {primaryLabel}
            </span>
          ) : null}
          {secondaryLabel && hasSecondary ? (
            <span className="flex items-center gap-1.5 text-content-secondary">
              <span className="size-2 rounded-full" style={{ backgroundColor: secondaryColor }} />
              {secondaryLabel}
            </span>
          ) : null}
        </div>
      ) : null}

      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        className="block"
        style={{ height }}
        role="img"
        aria-label={primaryLabel ?? 'Gráfico'}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Linhas de grade e rótulos do eixo vertical. */}
        {Array.from({ length: 5 }, (_, i) => {
          const value = scale.step * i
          const y = yAt(value)
          return (
            <g key={i}>
              <line
                x1={PADDING.left}
                y1={y}
                x2={width - PADDING.right}
                y2={y}
                stroke="var(--color-line)"
                strokeWidth="1"
              />
              <text
                x={PADDING.left - 8}
                y={y + 3.5}
                textAnchor="end"
                className="fill-[var(--color-content-muted)] text-[10px]"
              >
                {formatValue(value)}
              </text>
            </g>
          )
        })}

        {hasSecondary ? (
          <path
            d={buildPath('secondary')}
            fill="none"
            stroke={secondaryColor}
            strokeWidth="1.5"
            strokeDasharray="3 3"
            strokeLinejoin="round"
          />
        ) : null}

        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path
          d={buildPath('value')}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Rótulos do eixo horizontal, no máximo seis para não empilhar. */}
        {data.map((point, index) => {
          const every = Math.max(1, Math.ceil(data.length / 6))
          if (index % every !== 0 && index !== data.length - 1) return null
          return (
            <text
              key={index}
              x={xAt(index)}
              y={height - 6}
              textAnchor={index === 0 ? 'start' : index === data.length - 1 ? 'end' : 'middle'}
              className="fill-[var(--color-content-muted)] text-[10px]"
            >
              {point.label}
            </text>
          )
        })}

        {hover !== null ? (
          <g>
            <line
              x1={xAt(hover)}
              y1={PADDING.top}
              x2={xAt(hover)}
              y2={PADDING.top + innerHeight}
              stroke="var(--color-line-strong)"
              strokeWidth="1"
            />
            <circle cx={xAt(hover)} cy={yAt(data[hover]!.value)} r="4" fill={color} />
          </g>
        ) : null}

        {/* Faixas invisíveis capturam o ponteiro: mirar na linha exigiria
            precisão de pixel. */}
        {data.map((_, index) => (
          <rect
            key={index}
            x={xAt(index) - innerWidth / data.length / 2}
            y={PADDING.top}
            width={innerWidth / data.length}
            height={innerHeight}
            fill="transparent"
            onMouseEnter={() => setHover(index)}
          />
        ))}
      </svg>

      {hovered ? (
        /*
          A caixa segue o ponto, e o ponto não começa na borda: o eixo vertical
          come os primeiros pixels. A conta antiga ignorava essa margem e a
          escala do desenho, então a caixa aparecia deslocada do ponto que ela
          descrevia. Perto das bordas ela para de acompanhar para não sair pela
          lateral do card.
        */
        <div
          className="pointer-events-none absolute top-0 rounded-md bg-surface-deepest px-2 py-1.5 text-xs shadow-lg"
          style={{
            left: Math.min(width - 60, Math.max(60, xAt(hover!))),
            transform: 'translateX(-50%)',
          }}
        >
          <p className="font-medium text-content-primary">{formatValue(hovered.value)}</p>
          {hovered.secondary !== undefined ? (
            <p className="text-content-muted">{formatValue(hovered.secondary)}</p>
          ) : null}
          <p className="mt-0.5 text-[10px] text-content-muted">{hovered.label}</p>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Arco de progresso da cota mensal.
 *
 * Um arco em vez de barra porque a comparação relevante não é "quanto foi
 * usado" e sim "quanto falta", e o arco mostra o vazio com o mesmo destaque do
 * cheio. Muda de cor a partir do limiar de alerta.
 */
export function QuotaGauge({
  usedPercent,
  projectedPercent,
  alertPercent = 80,
  label,
  sublabel,
  size = 160,
}: {
  usedPercent: number
  projectedPercent?: number
  alertPercent?: number
  label: string
  sublabel?: string
  size?: number
}) {
  const strokeWidth = 12
  const radius = (size - strokeWidth) / 2
  const circumference = radius * Math.PI * 1.5

  const clamped = Math.min(100, Math.max(0, usedPercent))
  const projected = projectedPercent !== undefined ? Math.min(100, projectedPercent) : null

  const color =
    clamped >= 95
      ? 'var(--color-danger)'
      : clamped >= alertPercent
        ? 'var(--color-warning)'
        : 'var(--color-success)'

  const center = size / 2

  return (
    <div className="relative inline-flex flex-col items-center">
      <svg width={size} height={size * 0.82} viewBox={`0 0 ${size} ${size * 0.82}`}>
        {/* Arco de 270 graus, aberto embaixo. */}
        <g transform={`rotate(135 ${center} ${center})`}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--color-surface-active)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${circumference} ${circumference * 3}`}
          />

          {projected !== null && projected > clamped ? (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={color}
              strokeOpacity="0.28"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${(projected / 100) * circumference} ${circumference * 3}`}
            />
          ) : null}

          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${(clamped / 100) * circumference} ${circumference * 3}`}
            className="transition-[stroke-dasharray] duration-500"
          />
        </g>

        <text
          x={center}
          y={center - 2}
          textAnchor="middle"
          className="fill-[var(--color-content-primary)] text-2xl font-bold"
        >
          {clamped < 1 && clamped > 0 ? '<1' : Math.round(clamped)}%
        </text>
        <text
          x={center}
          y={center + 16}
          textAnchor="middle"
          className="fill-[var(--color-content-muted)] text-[10px]"
        >
          {label}
        </text>
      </svg>

      {sublabel ? (
        <p className="-mt-1 text-center text-[11px] text-content-muted">{sublabel}</p>
      ) : null}
    </div>
  )
}

/** Linha compacta sem eixos, para caber dentro de um cartão de indicador. */
export function Sparkline({
  values,
  height = 32,
  color = 'var(--color-brand)',
}: {
  values: number[]
  height?: number
  color?: string
}) {
  if (values.length < 2) return <div style={{ height }} />

  const width = 120
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const range = max - min || 1

  const path = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width
      const y = height - ((value - min) / range) * height
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} aria-hidden="true">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}

/** Barra horizontal simples para rankings, como consumo por usuário. */
export function BarRow({
  label,
  value,
  max,
  formatValue,
  color = 'var(--color-brand)',
}: {
  label: string
  value: number
  max: number
  formatValue: (value: number) => string
  color?: string
}) {
  const percent = max > 0 ? (value / max) * 100 : 0

  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-32 shrink-0 truncate text-[13px] text-content-secondary">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-active">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${percent}%`, backgroundColor: color }}
        />
      </div>
      <span className="w-20 shrink-0 text-right font-mono text-[11px] text-content-muted tabular-nums">
        {formatValue(value)}
      </span>
    </div>
  )
}

export function StatTile({
  label,
  value,
  sublabel,
  trend,
  accent,
  icon,
}: {
  label: string
  value: string
  sublabel?: string
  trend?: number[]
  accent?: 'brand' | 'success' | 'warning' | 'danger'
  icon?: React.ReactNode
}) {
  const accentColor = {
    brand: 'var(--color-brand)',
    success: 'var(--color-success)',
    warning: 'var(--color-warning)',
    danger: 'var(--color-danger)',
  }[accent ?? 'brand']

  return (
    <div className="rounded-xl bg-surface-raised p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold tracking-wide text-content-muted uppercase">
          {label}
        </p>
        {icon ? (
          <span className={cn('shrink-0 [&>svg]:size-4')} style={{ color: accentColor }}>
            {icon}
          </span>
        ) : null}
      </div>

      <p className="mt-1.5 text-2xl font-bold text-content-primary tabular-nums">{value}</p>
      {sublabel ? <p className="mt-0.5 text-xs text-content-secondary">{sublabel}</p> : null}

      {trend && trend.length > 1 ? (
        <div className="mt-2">
          <Sparkline values={trend} color={accentColor} />
        </div>
      ) : null}
    </div>
  )
}
